// src/component/MM2026JoinDialogLauncher.jsx
import React, { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../api/firebase";
import MM2026JoinDialog from "./MM2026JoinDialog";

const PAYMENT_PHASE_2026 = "4";

function isSameDay(d1, d2) {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

export default function MM2026JoinDialogLauncher() {
  const [open, setOpen] = useState(false);

  const [configReady, setConfigReady] = useState(false);
  const [userReady, setUserReady] = useState(false);

  const [enabled, setEnabled] = useState(true); // 管理画面でのON/OFF
  const [hasPhase4Payment, setHasPhase4Payment] = useState(false);

  // 設定ドキュメント購読
  useEffect(() => {
    const ref = doc(db, "systemConfig", "mm2026JoinDialog");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setEnabled(data.enabled !== false); // 未定義なら true
        } else {
          setEnabled(true);
        }
        setConfigReady(true);
      },
      (err) => {
        console.error("MM2026JoinDialogLauncher config error:", err);
        // エラー時はとりあえず true 扱い
        setEnabled(true);
        setConfigReady(true);
      }
    );

    return () => unsub();
  }, []);

  // ユーザードキュメント購読（支払いチェック）
  useEffect(() => {
    const current = auth.currentUser;
    if (!current) {
      setUserReady(true);
      return;
    }

    const ref = doc(db, "users", current.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const paid =
            Array.isArray(data.payments) &&
            data.payments.some(
              (p) =>
                p.paymentPhase === PAYMENT_PHASE_2026 &&
                (p.paymentMethod === "銀行振込" ||
                  p.paymentMethod === "クレジットカード")
            );
          setHasPhase4Payment(paid);
        } else {
          setHasPhase4Payment(false);
        }
        setUserReady(true);
      },
      (err) => {
        console.error("MM2026JoinDialogLauncher user error:", err);
        setUserReady(true);
      }
    );

    return () => unsub();
  }, []);

  // 両方揃ったら「出す／出さない」を決定
  useEffect(() => {
    if (!configReady || !userReady) return;

    const current = auth.currentUser;
    if (!current) return;

    // 管理画面で「OFF」なら何もしない
    if (!enabled) {
      setOpen(false);
      return;
    }

    // 4期支払い済みなら出さない
    if (hasPhase4Payment) {
      setOpen(false);
      return;
    }

    // 1日1回チェック（ローカルストレージ）
    const uid = current.uid;
    const key = `mm2026JoinDialogLastShown_${uid}`;
    const last = localStorage.getItem(key);
    const today = new Date();

    let shouldOpen = false;

    if (!last) {
      shouldOpen = true;
    } else {
      const lastDate = new Date(last);
      if (!isSameDay(lastDate, today)) {
        shouldOpen = true;
      }
    }

    if (shouldOpen) {
      setOpen(true);
      localStorage.setItem(key, today.toISOString());
    }
  }, [configReady, userReady, enabled, hasPhase4Payment]);

  const handleClose = () => {
    const uid = auth.currentUser?.uid;
    if (uid) {
      const key = `mm2026JoinDialogLastShown_${uid}`;
      localStorage.setItem(key, new Date().toISOString());
    }
    setOpen(false);
  };

  if (!configReady || !userReady) return null;

  return <MM2026JoinDialog open={open} onClose={handleClose} />;
}
